import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Stack,
  Button,
  Checkbox,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@altimateai/lego";
import { RunMetadata, getAdeBenchRunResults, deleteAdeBenchRun } from "../../services/adeBench";
import { TrialTable } from "./TrialTable";
import { Loader } from "../../components/Loader";
import { AccuracyBadge } from "./AccuracyBadge";
import { PassAtKDisplay } from "./PassAtKDisplay";

interface RunRowProps {
  run: RunMetadata;
  isExpanded: boolean;
  isSelected: boolean;
  onRunClick: (runId: string) => void;
  onRunSelect: (runId: string, checked: boolean) => void;
}

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const RunRow: React.FC<RunRowProps> = ({
  run,
  isExpanded,
  isSelected,
  onRunClick,
  onRunSelect,
}) => {
  const [hasTriedFetch, setHasTriedFetch] = useState(false);
  const queryClient = useQueryClient();

  const {
    data: runResults,
    isLoading: resultsLoading,
    error: resultsError,
    isFetched,
  } = useQuery({
    queryKey: ["adeBenchRunResults", run.run_id],
    queryFn: () => getAdeBenchRunResults(run.run_id),
    enabled: isExpanded && !hasTriedFetch,
    staleTime: 5 * 60 * 1000,
  });

  // Handle onSettled behavior (TanStack Query v5 removed onSettled from useQuery)
  useEffect(() => {
    if (isFetched) {
      setHasTriedFetch(true);
    }
  }, [isFetched]);

  const deleteMutation = useMutation({
    mutationFn: () => deleteAdeBenchRun(run.run_id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adeBenchRuns"] });
      queryClient.invalidateQueries({ queryKey: ["adeBenchProgress"] });
    },
  });

  return (
    <>
      <tr
        className={`al-border-b al-cursor-pointer hover:al-bg-gray-50 ${isExpanded ? "al-bg-blue-50" : ""} ${isSelected ? "al-bg-blue-100" : ""}`}
        onClick={() => onRunClick(run.run_id)}
      >
        <td className="al-p-3 al-text-center" onClick={e => e.stopPropagation()}>
          <Checkbox
            checked={isSelected}
            onCheckedChange={(checked: boolean | "indeterminate") =>
              onRunSelect(run.run_id, checked as boolean)
            }
          />
        </td>
        <td className="al-p-3 al-text-center al-text-gray-400">{isExpanded ? "▼" : "▶"}</td>
        <td className="al-p-3 al-text-sm al-text-gray-600">{formatDate(run.start_time)}</td>
        <td className="al-p-3">
          <span className="al-font-mono al-text-sm">{run.run_id}</span>
        </td>
        <td className="al-p-3">
          <span className="al-font-medium">{run.agent_name}</span>
        </td>
        <td className="al-p-3">
          <span className="al-text-sm al-text-gray-600">{run.model_name || "-"}</span>
        </td>
        <td className="al-p-3">
          <AccuracyBadge accuracy={run.accuracy} />
        </td>
        <td className="al-p-3">
          <Stack direction="row" gap="xs" align="center">
            <span className="al-text-green-600 al-font-medium">{run.n_resolved ?? 0}</span>
            <span className="al-text-gray-400">/</span>
            <span className="al-text-gray-600">{run.tasks_attempted ?? run.dataset_size}</span>
          </Stack>
        </td>
        <td className="al-p-3">
          <PassAtKDisplay passAtK={run.pass_at_k} />
        </td>
        <td className="al-p-3">
          <span className="al-text-sm al-text-gray-500">{run.username}</span>
        </td>
      </tr>

      {isExpanded && (
        <tr className="al-bg-gray-50">
          <td colSpan={10} className="al-p-0">
            <div className="al-p-4 al-border-l-4 al-border-blue-500 al-ml-4">
              {resultsLoading ? (
                <Loader label="Loading trial details..." />
              ) : resultsError ? (
                <div className="al-text-red-600 al-text-sm">Failed to load trial details</div>
              ) : runResults ? (
                <Stack direction="column" gap="md">
                  <Stack
                    direction="row"
                    gap="lg"
                    align="center"
                    className="al-text-sm al-text-gray-600"
                  >
                    <span>
                      <strong>Tasks Resolved:</strong> {runResults.n_resolved} /{" "}
                      {new Set(runResults.results.map(r => r.task_id)).size}
                    </span>
                    <span>
                      <strong>Trials:</strong> {runResults.results.length}
                    </span>
                    <span>
                      <strong>Accuracy:</strong> {(runResults.accuracy * 100).toFixed(1)}%
                    </span>
                    {runResults.pass_at_k && Object.keys(runResults.pass_at_k).length > 0 && (
                      <span>
                        <strong>Pass@k:</strong>{" "}
                        {Object.entries(runResults.pass_at_k)
                          .filter(([, v]) => v !== null)
                          .sort(([a], [b]) => parseInt(a) - parseInt(b))
                          .map(([k, v]) => `@${k}: ${((v as number) * 100).toFixed(0)}%`)
                          .join(", ")}
                      </span>
                    )}
                    {run.total_cost_usd && (
                      <span>
                        <strong>Cost:</strong> ${run.total_cost_usd.toFixed(4)}
                      </span>
                    )}
                    {run.total_tokens && (
                      <span>
                        <strong>Tokens:</strong> {run.total_tokens.toLocaleString()}
                      </span>
                    )}
                    <div className="al-ml-auto">
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="destructive"
                            size="sm"
                            disabled={deleteMutation.isPending}
                            onClick={e => e.stopPropagation()}
                          >
                            {deleteMutation.isPending ? "Deleting..." : "Delete Run"}
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Run</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete run{" "}
                              <code className="al-bg-gray-100 al-px-1 al-rounded">
                                {run.run_id}
                              </code>
                              ? This will permanently delete all trial data and cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => deleteMutation.mutate()}
                              className="al-bg-red-600 hover:al-bg-red-700"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </Stack>
                  <TrialTable results={runResults.results} />
                </Stack>
              ) : (
                <div className="al-text-gray-500 al-text-sm">No trial data available</div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};
